import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniOtpInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography/UniTypography';
import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { StackActions, useNavigation } from '@react-navigation/native';
import { useUtxUsersGetUserProfile } from '@utx/utx-client';
import { Pressable, SxProp, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import _, { isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { OtpInput } from '../../enum';
import { useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { useVerifyOtp } from '../../hooks/useTfaVerify.hook';
import { BillPaymentConfirmProp, BillPaymentStackRoutes } from '../../payment.navigator';
import { errorParser } from '../otp/type';

interface ConfirmSmsOtpProps {
  bottomsheetRef: React.RefObject<BottomSheet>;
  dataProp: BillPaymentConfirmProp;
  onClose: () => void;
  documentSelected?: DocumentPicker.DocumentResult;
  userFee: string;
}

export const ConfirmSmsOtp: React.FC<ConfirmSmsOtpProps> = (props) => {
  const { bottomsheetRef, dataProp, onClose, documentSelected, userFee } = props;
  const { request, response, debitAccount } = dataProp;
  const navigation = useNavigation();
  const [isShowingHandle, setShowingHandle] = useState(true);
  const [countdownTime, setCountdownTime] = useState<number>(15);
  const { t } = useTranslation('global-bill-payment');
  const {
    general: { alertMessage, setAlertMessage },
    postCreateBillPayment: { postSubmit, mapInstructionSubmitRequest, isLoadingSubmit }
  } = useCreateBillPayment();
  const {
    smsVerify: { postSendSmsVerificationApi }
  } = useVerifyOtp(dataProp.request.id!);

  const [pageState, setPageState] = useState<{
    ready?: boolean;
    code?: string;
    errorMessage?: string;
    requestAgain?: boolean;
  }>({});

  const queryProfile = useUtxUsersGetUserProfile({
    query: {
      enabled: false
    }
  });

  const mobile = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.loginPhoneNumber;
    }
    return '';
  }, [queryProfile.data?.data]);

  const countryCode = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.countryCode;
    }
    return 852;
  }, [queryProfile.data?.data]);

  const callRefresh = () => {
    queryProfile.refetch();
    postSendSmsVerificationApi(
      (data) => undefined,
      (error) => undefined
    );
  };

  useEffect(() => {
    let number = countdownTime;
    let status = pageState?.requestAgain;
    if (number === 0) {
      status = false;
      setPageState({ ...pageState, requestAgain: false });
      setCountdownTime(15);
    }
    var timer: ReturnType<typeof setTimeout>;
    if (number > 0 && status) {
      timer = setTimeout(() => {
        --number;
        setCountdownTime(number);
      }, 1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [pageState?.requestAgain, countdownTime]);

  useEffect(() => {
    if (pageState && pageState.code && pageState.ready) {
      onConfirm(pageState.code);
    }
  }, [pageState?.code, pageState?.ready]);

  const sendRequestAgain = () => {
    setPageState({ ...pageState, requestAgain: true });
    postSendSmsVerificationApi(
      (data) => undefined,
      (error) => undefined
    );
  };

  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const renderBackdrop = useCallback(
    (props) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} pressBehavior={'none'} enableTouchThrough={true} />
    ),
    []
  );

  const onConfirm = (code?: string) => {
    setPageState({ ...pageState, errorMessage: '' });
    setAlertMessage('');
    if (request && response) {
      let otpCode = pageState && pageState.code ? pageState.code : '';
      if (!pageState.ready) {
        setPageState({ ...pageState, errorMessage: t('global-bill-payment::otp.error--otp-invalid') });
      } else {
        postSubmit(
          mapInstructionSubmitRequest(request, response, code ?? otpCode),
          (submitData) => {
            onClose();
            setTimeout(() => {
              navigation.dispatch(
                StackActions.replace(RootStackRoutes.BillPayment, {
                  screen: BillPaymentStackRoutes.Success,
                  params: {
                    request,
                    response,
                    debitAccount,
                    submitResponse: submitData.data,
                    documentSelected: documentSelected,
                    paymentType: dataProp.paymentType,
                    userFee
                  }
                })
              );
            }, 200);
          },
          (error) => {
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
            setPageState({ ...pageState, errorMessage: errorMessage });
          }
        );
      }
    }
  };

  return (
    <BottomSheet
      index={-1}
      ref={bottomsheetRef}
      snapPoints={animatedSnapPoints}
      handleHeight={animatedHandleHeight}
      contentHeight={animatedContentHeight}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      onClose={onClose}
      onChange={(index: number) => {
        const opening = index === 1;
        setShowingHandle(opening);
        if (opening) {
          callRefresh();
        }
      }}
      handleComponent={() => (
        <View sx={sxStyles.bottomSheetHeadWrapper}>
          {isShowingHandle && <View sx={sxStyles.viewHandle} />}
          <View sx={sxStyles.bottomsheetHeadHandler}></View>
        </View>
      )}>
      <BottomSheetScrollView onLayout={handleContentLayout} scrollEnabled={true}>
        <View sx={sxStyles.container}>
          <View sx={sxStyles.bodyWrapper}>
            <View sx={sxStyles.containerItem}>
              <BottomSheetTopHeader title={t('global-bill-payment::glossary.title--sms-authentication')} onPress={onClose} />
            </View>
            <View sx={sxStyles.body}>
              <View sx={sxStyles.iconView}>
                <Icon name="sms2FA" height={80} width={80} />
              </View>
              <TextBody sx={sxStyles.titleText}>{t('global-bill-payment::sms.text--protecting-your-account')}</TextBody>
              <View sx={sxStyles.otpView}>
                <TextCaption semiBold sx={sxStyles.otpText}>
                  {t('global-bill-payment::authentication.sms.text--enter-code')}
                </TextCaption>
                <UniOtpInput
                  errorText={pageState && !_.isEmpty(pageState.errorMessage) ? pageState.errorMessage : undefined}
                  maxLength={OtpInput.MAX_LENGTH}
                  onValueChange={(code) => {
                    setPageState({ ...pageState, code, ready: code.length === OtpInput.MAX_LENGTH });
                  }}
                />
                <View sx={sxStyles.requestAgainView}>
                  <TextCaption sx={sxStyles.secondaryColorText}>
                    {t('log-in::sign-up.text--verification-code-will-be-sent-to')}{' '}
                    <TextCaption semiBold sx={sxStyles.secondaryColorText}>
                      ({countryCode}){mobile.replace(mobile.substring(mobile.length - 6, mobile.length - 2), '****')}
                    </TextCaption>
                  </TextCaption>
                  <View sx={sxStyles.requestNewCodeView}>
                    <TextCaption sx={sxStyles.secondaryColorText}>
                      {t('log-in::sign-up.text--can-not-receive')}{' '}
                      {!pageState?.requestAgain && (
                        <TextCaption sx={sxStyles.textRequestAgain} onPress={() => sendRequestAgain()}>
                          {t('log-in::sign-up.text--request-again')}
                        </TextCaption>
                      )}
                      {pageState?.requestAgain && (
                        <TextCaption sx={sxStyles.requestAgainText}>
                          {t('log-in::sign-up.text--request-new-one')} {countdownTime} {t('log-in::sign-up.text--second')}
                        </TextCaption>
                      )}
                    </TextCaption>
                  </View>
                </View>
                {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
              </View>
            </View>
            <View sx={sxStyles.button}>
              <View sx={sxStyles.flex220}>
                <UniButton
                  label={t('global-bill-payment::glossary.confirm')}
                  onPress={() => onConfirm(pageState.code)}
                  sx={sxStyles.viewButtonWrapper}
                  size="medium"
                  loading={isLoadingSubmit}
                  disabled={isLoadingSubmit || !pageState.ready}
                />
              </View>
            </View>
          </View>
        </View>
      </BottomSheetScrollView>
    </BottomSheet>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    backgroundColor: '$backgroundPrimary'
  },
  containerItem: {
    flex: 1
  },
  bottomSheetHeadWrapper: {
    alignItems: 'center',
    flexDirection: 'column'
  },
  viewHandle: {
    width: '$12',
    height: '$1',
    backgroundColor: '$backgroundPrimary',
    borderRadius: 3,
    position: 'absolute',
    top: -10
  },
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  },
  button: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: '$5'
  },
  bodyWrapper: {
    flexDirection: 'column',
    mr: '$4',
    ml: '$4',
    mb: '$3'
  },
  viewButtonWrapper: {
    height: '$11',
    width: '100%'
  },
  flex220: {
    flex: 220
  },
  body: {
    pt: '$7',
    zIndex: 2,
    alignItems: 'center'
  },
  iconView: {
    height: '$20',
    width: '$20',
    mb: '$2'
  },
  otpView: {
    mt: '$10'
  },
  titleText: {
    textAlign: 'center'
  },
  requestAgainView: {
    alignItems: 'center',
    mt: '$3'
  },
  requestNewCodeView: {
    width: '77%',
    alignItems: 'center',
    mt: '$5'
  },
  requestAgainText: {
    ml: '$2',
    mt: '$5',
    textAlign: 'center',
    color: '$textSecondary'
  },
  secondaryColorText: {
    color: '#666666'
  },
  textRequestAgain: {
    color: '#DC9B32'
  },
  otpText: {
    mb: '$1'
  },
  viewAlert: {
    mt: '$3'
  }
};

interface BottomSheetTopHeaderProps {
  title: string;
  onPress: () => void;
}

export const BottomSheetTopHeader: React.FC<BottomSheetTopHeaderProps> = (props) => {
  const { title, onPress } = props;

  return (
    <View sx={sxTopHeader.container}>
      <TextTitle semiBold sx={sxTopHeader.title}>
        {title}
      </TextTitle>
      <Pressable onPress={onPress} sx={sxTopHeader.iconClose}>
        <View>
          <Icon name="cross" themeColor="$iconDefault" />
        </View>
      </Pressable>
    </View>
  );
};

const sxTopHeader: Record<string, SxProp> = {
  container: {
    flexDirection: 'row'
  },
  title: {
    flex: 4
  },
  iconClose: {
    flex: 1,
    alignContent: 'flex-end',
    alignItems: 'flex-end'
  }
};
